import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';

import {
    QuestionMarkButton,
    noticeError,
    Sizes,
    DefaultIntlFormatValues,
    useModal,
} from '@ratehub/base-ui';

import PRODUCT_TYPES from '../definitions/ProductTypes';
import FOCUS_VARIANTS from '../definitions/FocusVariants';
import getExplanationModal from '../functions/getCardLayoutExplanationModal';
import CreditCardSynopsisShape from '../definitions/CreditCardSynopsisShape';


function CardLayoutFocusText({
    cardInfo,
    productType,
    focusVariant,
    className,
    isCIBC = false,
}) {
    const modal = useModal();

    const ExplanationModal = getExplanationModal(productType);
    const bigNumberAndDescription = getBigNumberAndDescription({ productType, cardInfo, focusVariant, isCIBC });

    function handleExplanationClick() {
        modal.open({
            screenReaderLabel: 'Card Calculation Explanation',
            showBranding: false,
            isContentFullWidth: true,
            verticalAlignment: 'flex-start',
            children: (
                <ExplanationModal
                    cardInfo={cardInfo}
                    isOpen={true}
                    onClose={() => {
                        modal.close();
                    }}
                />
            ),
        });
    }

    return (
        <div className={classNames(className, 'rh-pt-0_25')}>
            <div className="big-number-container rh-display-flex rh-align-items-center">
                <output
                    className="big-number rh-title-m weight-bold rh-display-inline-block rh-mb-0"
                    data-test-name="focus-text-big-number"
                >
                    {bigNumberAndDescription.bigNumber}
                </output>
                <If condition={ExplanationModal}>
                    <QuestionMarkButton
                        className="question-mark-button"
                        buttonSize={Sizes.ICONS['XS']}
                        onClick={handleExplanationClick}
                        data-name="card-calculation-button"
                    />
                </If>
            </div>

            <p className="big-number-description rh-text-s rh-m-0">
                {bigNumberAndDescription.description}
            </p>
        </div>
    );
}

CardLayoutFocusText.propTypes = {
    cardInfo: PropTypes.shape(CreditCardSynopsisShape).isRequired,
    productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)),
    focusVariant: PropTypes.oneOf(Object.values(FOCUS_VARIANTS)),
    className: PropTypes.string,
    isCIBC: PropTypes.bool,
};


function getBigNumberAndDescription({ productType, cardInfo, focusVariant, isCIBC }) {
    const {
        netFirstYearValue,
        netSubsequentYearValue,
        monthlySpendingAverage,
        annualFee,
        purchaseInterest,
        balanceTransferPromoInterest,
        balanceTransferPromoMonths,
        balanceTransferInterest,
        minimumDeposit,
    } = cardInfo;

    switch (productType) {
        case PRODUCT_TYPES.TRAVEL:
        case PRODUCT_TYPES.CASH_BACK:
        case PRODUCT_TYPES.ANY_REWARDS:
        case PRODUCT_TYPES.STORE_CREDIT:
            return {
                bigNumber: (
                    <FormattedMessage
                        id="edb.credit-card.per-year-reward"
                        defaultMessage="{perYearReward, number, CAD0}<per-year>/yr</per-year>"
                        values={{
                            perYearReward: focusVariant === FOCUS_VARIANTS.subsequentYearRewards
                                ? netSubsequentYearValue
                                : netFirstYearValue,
                            // eslint-disable-next-line react/jsx-one-expression-per-line
                            'per-year': str => <span className="rh-title-xs weight-bold">{str}</span>,
                        }}
                    />
                ),
                // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
                description: isCIBC
                    ? (
                        <FormattedMessage
                            id="edb.credit-card.spending-per-month.cibc"
                            defaultMessage={`based on spending <b>{monthlySpendingAverage, number, CAD0}/mo</b>
                            after the annual fee`}
                            values={{
                                monthlySpendingAverage,
                                ...DefaultIntlFormatValues,
                            }}
                        />
                    )
                    : (
                        <FormattedMessage
                            id="edb.credit-card.spending-per-month"
                            defaultMessage={`based on spending <b>{monthlySpendingAverage, number, CAD0}/mo</b>
                                after <b>{annualFee, number, CAD0}</b> annual fee`}
                            values={{
                                monthlySpendingAverage,
                                annualFee,
                                ...DefaultIntlFormatValues,
                            }}
                        />
                    ),
            };

        case PRODUCT_TYPES.BALANCE_TRANSFER:
            return {
                bigNumber: (
                    <FormattedNumber
                        style="percent"
                        value={balanceTransferPromoInterest / 100}
                        minimumFractionDigits={2}
                    />
                ),
                description: (
                    <FormattedMessage
                        id="edb.credit-card.purchase-interest-rate.balance-transfer"
                        defaultMessage={
                            `for the first {balanceTransferPromoMonths, plural,
                                one {month}
                                other {# months}
                            }. <strong>{balanceTransferInterest, number, percent2}</strong> after promo.`
                        }
                        values={{
                            balanceTransferPromoMonths,
                            balanceTransferInterest: balanceTransferInterest / 100,
                            ...DefaultIntlFormatValues,
                        }}
                    />
                ),
            };

        case PRODUCT_TYPES.LOW_INTEREST:
            return {
                bigNumber: (
                    <FormattedNumber
                        style="percent"
                        value={purchaseInterest / 100}
                        minimumFractionDigits={2}
                    />
                ),
                description: (
                    <FormattedMessage
                        id="edb.credit-card.purchase-interest-rate"
                        defaultMessage="Purchase interest rate"
                    />
                ),
            };

        case PRODUCT_TYPES.SECURED:
            return {
                bigNumber: (
                    <FormattedNumber
                        style="currency"
                        format="CAD0"
                        value={minimumDeposit}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                    />
                ),
                description: (
                    <FormattedMessage
                        id="edb.credit-card.secured.minimum-security-deposit"
                        defaultMessage="Minimum security deposit"
                    />
                ),
            };

        default:
            noticeError(new RangeError(`[getBigNumberAndDescription] unsupported credit card productType: “${productType}”.`));
            break;
    }
}


export default styled(observer(CardLayoutFocusText))`
    > .big-number-container {
        .question-mark-button { /* required CSS overrides for QMB */
            margin-left: ${Sizes.SPACING.HALF};
            margin-right: ${Sizes.SPACING.ONE};
        }
    }

    > .big-number-description {
        flex: 1 1 150px;
        width: 100%;
    }
`;
